import { PageCard } from "src/common/styles/styles"
import { PayableContractForm } from "../PayableContractForm"
import { PayableContract } from "src/common/models/payableContract"
import { CancelConfirmationModal } from "src/common/descriptions/components/cancelConfirmationModal"
import { ModalSuccess } from "src/components/ModalSuccess"
import { useEffect, useState } from "react"
import { SystemDescriptions } from "src/common/descriptions"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate, useParams } from "react-router"
import { Urls } from "src/common/urls"
import { cleanCurrentPayableContract, createPayableContractApi, getPayableContractByIdApi, updatePayableContractApi } from "../state/action"
import { RootState } from "src/state/reducer"
import { PayableContractError, PayableContractErrorOrigin } from "../state/types"
import { serviceLoading } from "src/common/apiLoader/state/selection"
import { CREATE_PAYABLE_CONTRACT_API, GET_PAYABLE_CONTRACT_BY_ID_API, UPDATE_PAYABLE_CONTRACT_API } from "../state/actionTypes"
import { Button, notification } from "antd"
import { DestroyConfirmationModal } from "src/components/DestroyConfirmationModal"
import { Loader } from "src/components/Loader"
import { ActivityLogAction } from "src/common/models/activityLog"

interface ReduxProps {
    error?: PayableContractError
    isCreatingPayableContract: boolean
    createPayableContractSuccess: boolean
    updatePayableContractSuccess: boolean
    isUpdatingPayableContract: boolean
    isGettingPayableContract: boolean
    currentPayableContract?: PayableContract
}

export const CreatePayableContract = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()

    const [modalConfirmationOpen, setModalConfirmationOpen] = useState<boolean>(false)
    const [modalCancelOpen, setModalCancelOpen] = useState<boolean>(false)
    const [formValues, setFormValues] = useState<PayableContract | undefined>()
    const [actionButtonClicked, setActionButtonClicked] = useState<boolean>(false)
    const [destroyConfirmationOpen, setDestroyConfirmationOpen] = useState<boolean>(false)

    const { id: payableContractId } = useParams<{ id: string }>();

    const reduxProps: ReduxProps = useSelector((state: RootState) => ({
        error: state.payableContract.error,
        isCreatingPayableContract: serviceLoading(state, [CREATE_PAYABLE_CONTRACT_API]),
        createPayableContractSuccess: state.payableContract.createPayableContractSuccess,
        updatePayableContractSuccess: state.payableContract.updatePayableContractSuccess,
        isUpdatingPayableContract: serviceLoading(state, [UPDATE_PAYABLE_CONTRACT_API]),
        isGettingPayableContract: serviceLoading(state, [GET_PAYABLE_CONTRACT_BY_ID_API]),
        currentPayableContract: state.payableContract.currentPayableContract,
    }))

    const descriptions = SystemDescriptions.PAGES.PAYABLE_CONTRACT.CREATE_PAYABLE_CONTRACT
    const commonDescriptions = SystemDescriptions.PAGES.COMMON

    useEffect(() => {
        if (reduxProps.currentPayableContract && !payableContractId) {
            dispatch(cleanCurrentPayableContract())
        }
        if (payableContractId) {
            dispatch(getPayableContractByIdApi(Number(payableContractId)))
        }
    }, [])

    const openNotification = (message: string, description: string) => {
        notification.success({
            message,
            description,
        });
    };

    useEffect(() => {
        if (reduxProps.updatePayableContractSuccess && actionButtonClicked) {
            closeModal()

            openNotification(
                "Exito",
                "Contrato actualizado",
            )

            navigate(Urls.FRONTEND.ACCOUNT_PAYABLE.PAYABLE_CONTRACTS)
        }
    }, [reduxProps.updatePayableContractSuccess])

    const onFinish = (payableContract: Partial<PayableContract>): void => {
        setFormValues(payableContract as PayableContract)
        setModalConfirmationOpen(true)
    }

    const onCancel = () => {
        setModalCancelOpen(true)
    }

    const handleConfirmationAccept = () => {
        setModalConfirmationOpen(false)
        setActionButtonClicked(true)
        if (payableContractId) {
            dispatch(updatePayableContractApi({
                ...formValues as PayableContract,
                characteristics: reduxProps.currentPayableContract?.characteristics || [],
                id: Number(payableContractId),
                updateAction: ActivityLogAction.UPDATE,
            }))
        } else {
            dispatch(createPayableContractApi(formValues as PayableContract))
        }
    }

    const handleCancelAccept = () => {
        navigate(Urls.FRONTEND.ACCOUNT_PAYABLE.PAYABLE_CONTRACTS)
    }

    const closeModal = () => {
        dispatch(cleanCurrentPayableContract())
        setDestroyConfirmationOpen(false)
    }

    const renderModals = () => (
        <>
            <CancelConfirmationModal
                isVisible={modalConfirmationOpen}
                onAccept={handleConfirmationAccept}
                onCancel={() => {
                    setModalConfirmationOpen(false)
                }}
                title={descriptions.CONFIRM_MODAL.TITLE}
                okText={descriptions.CONFIRM_MODAL.ACTION_BUTTON}
                cancelText={descriptions.CONFIRM_MODAL.CANCEL_BUTTON}
                description={descriptions.CONFIRM_MODAL.DESCRIPTION}
            />
            <CancelConfirmationModal
                isVisible={modalCancelOpen}
                onAccept={handleCancelAccept}
                onCancel={() => { setModalCancelOpen(false) }}
                title={descriptions.CANCEL_MODAL.TITLE}
                okText={descriptions.CANCEL_MODAL.ACTION_BUTTON}
                cancelText={descriptions.CANCEL_MODAL.CANCEL_BUTTON}
                description={descriptions.CANCEL_MODAL.DESCRIPTION}
            />
            <ModalSuccess
                isVisible={(reduxProps.createPayableContractSuccess) && actionButtonClicked}
                title={descriptions.SUCCESS_MODAL.TITLE}
                description={descriptions.SUCCESS_MODAL.DESCRIPTION}
                onPrimaryAction={handleCancelAccept}
                onSecondaryActiont={handleCancelAccept}
                primaryLabel={descriptions.SUCCESS_MODAL.PRIMARY_LABEL}
                secondaryLabel={descriptions.SUCCESS_MODAL.SECONDARY_LABEL}
                hideSecondaryButton={true}
            />
            <DestroyConfirmationModal
                open={destroyConfirmationOpen}
                title={descriptions.DESTROY.TITLE}
                description={descriptions.DESTROY.DESCRIPTION}
                onCancel={() => {
                    closeModal()
                }}
                onAccept={() => {
                    if (reduxProps.currentPayableContract) {
                        setActionButtonClicked(true)
                        dispatch(updatePayableContractApi({
                            ...reduxProps.currentPayableContract,
                            updateAction: ActivityLogAction.INACTIVATED,
                        }))
                    }
                }}
                actionLabel={commonDescriptions.ACCEPT}
                destroyKeyLabel={descriptions.DESTROY.FORM.CONFIRMATION.PLACEHOLDER}
                destroyKeyValue={descriptions.DESTROY.INACTIVATE_KEY}
                acceptText={commonDescriptions.ACCEPT}
                cancelText={commonDescriptions.CANCEL}
                isLoading={reduxProps.isUpdatingPayableContract}
                errorMessage={
                    reduxProps.error?.type === PayableContractErrorOrigin.UPDATE_PAYABLE_CONTRACT
                        ? reduxProps.error.detail?.message
                        : undefined
                }
            />
        </>
    )

    const renderLoader = () => (
        <Loader
            isVisible={reduxProps.isGettingPayableContract}
            title={descriptions.MAIN_ACTION_TITLE}
            description={descriptions.MAIN_ACTION_DESCRIPTION}
        />
    )

    // TODO: ADD DESCRIPTION LANG
    const renderExtra = () => {
        return <Button
            color={
                reduxProps.currentPayableContract?.isActive
                    ? "danger"
                    : "primary"
            }
            variant="filled"
            size="small"
            onClick={() => {
                setDestroyConfirmationOpen(true)
            }}
        >
            {
                reduxProps.currentPayableContract?.isActive
                    ? "Pausar Contrato"
                    : "Reactivar Contrato"
            }

        </Button>
    }

    return (
        <>
            <PageCard
                size="small"
                title={descriptions.TITLE}
                extra={payableContractId && renderExtra()}
            >
                <PayableContractForm
                    onFinish={onFinish}
                    onCancel={onCancel}
                    initialValues={reduxProps.currentPayableContract}
                />
            </PageCard>
            {renderModals()}
            {renderLoader()}
        </>
    )
}
